/* "Pick up where you left off" + "Recent searches" for the header search panel.
 * Owner reference: Chewy's search dropdown (2026-09-04). Their third section, "Popular Articles",
 * was explicitly not wanted.
 *
 * Both lists are per-visitor, so they cannot be server-rendered — Liquid has no idea what this
 * person looked at. This script records the two events in localStorage and injects the sections
 * into the panel that predictive-search.js already builds for the empty state, ABOVE the curated
 * "Popular searches"/"Popular products" it renders today. Those stay: they are the fallback for a
 * first-time visitor, who has neither history.
 *
 * Loaded from sections/header.liquid on every page, because the RECORDING half has to run on
 * product and search pages, not just wherever the panel opens.
 */
(function () {
  var VIEW_KEY = 'llm_recently_viewed';
  var TERM_KEY = 'llm_recent_searches';
  var MAX_VIEWS = 12;
  var MAX_TERMS = 6;

  /* Every storage call is wrapped. localStorage throws outright in Safari private mode and when a
     customer has blocked site data — an exception here would take the whole search panel down with
     it, which is a far worse outcome than losing a convenience list. */
  function read(key) {
    try {
      var raw = localStorage.getItem(key);
      var val = raw ? JSON.parse(raw) : [];
      return Array.isArray(val) ? val : [];
    } catch (e) { return []; }
  }
  function write(key, list) {
    try { localStorage.setItem(key, JSON.stringify(list)); } catch (e) { /* quota or blocked */ }
  }

  // ---- recording -------------------------------------------------------------------------

  function meta(prop) {
    var el = document.querySelector('meta[property="' + prop + '"]');
    return el ? el.getAttribute('content') : '';
  }

  /* og:title carries the shop suffix ("NorthShore MEGAMAX Adult Diapers | LL Medico"), which would
     ride along into every tile's tooltip and alt text. Cut it by matching og:site_name rather than
     a hardcoded string, and only when a separator precedes it — so a product that legitimately ends
     in those words keeps its name. */
  function trimSite(title) {
    var site = meta('og:site_name');
    if (!site) return title;
    var i = title.lastIndexOf(site);
    if (i > 0 && /[|–—-]\s*$/.test(title.slice(0, i))) {
      return title.slice(0, i).replace(/\s*[|–—-]\s*$/, '').trim();
    }
    return title;
  }

  /* Read from og: tags rather than the theme's product markup. The tags are emitted by Shopify on
     every product page regardless of template, so this keeps working if the PDP is restructured —
     and this file should never be a reason a product page breaks. */
  function recordView() {
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    if (!m) return;
    /* The path is not enough. A mistyped or retired handle still serves under /products/, and the
       404 page's og:title is "404 Not Found" — which got stored as a viewed product, complete with
       a tile linking back to the 404 (caught in testing 2026-09-04). Shopify only sets
       og:type=product on a real product page, so that is the gate. */
    if (meta('og:type') !== 'product') return;
    var handle = m[1];
    var title = meta('og:title') || (document.querySelector('h1') || {}).textContent || '';
    var image = meta('og:image');
    title = trimSite(String(title).trim());
    if (!title) return;

    var list = read(VIEW_KEY).filter(function (v) { return v && v.handle !== handle; });
    list.unshift({ handle: handle, title: title, image: image, url: '/products/' + handle });
    write(VIEW_KEY, list.slice(0, MAX_VIEWS));
  }

  /* Newest first, one entry per term regardless of casing. Length-capped: the box accepts a paste
     of any size and a runaway string would sit in storage and in the panel forever. */
  function storeTerm(q) {
    q = String(q == null ? '' : q).trim().slice(0, 100);
    if (!q) return;
    var lower = q.toLowerCase();
    var list = read(TERM_KEY).filter(function (t) { return String(t).toLowerCase() !== lower; });
    list.unshift(q);
    write(TERM_KEY, list.slice(0, MAX_TERMS));
  }

  /* TWO recording points, because neither covers the other:

     1. Landing on /search — catches the "View all results" button, a suggestion chip that routes
        through the results page, a bookmarked or shared search URL.
     2. Submitting the form — catches the case the owner hit: type a query, click a product straight
        out of the dropdown, never land on /search. Without this the search is never recorded even
        though the customer clearly made one.

     Both can fire for the same search; storeTerm dedupes case-insensitively, so it stays one entry. */
  function recordTerm() {
    if (!/\/search/.test(location.pathname)) return;
    var q = new URLSearchParams(location.search).get('q');
    storeTerm(q);
    /* The results page is the only place we know the RESULT COUNT, and the zero-result list is the
       most useful thing this logging produces — so read it off the rendered page. Meilisearch owns
       the count line, so wait for it rather than reading a skeleton: an unresolved "0" logged as a
       zero-result search would poison exactly the report it exists to feed. */
      waitForCount(function (hits) { report(q, hits, 'results'); });
  }

  /* ---- search-query logging (worker -> D1) + Klaviyo -----------------------------------------
     Nothing recorded what customers search for; queries went to Meilisearch and vanished. Both
     sinks are strictly fire-and-forget: a search must never be delayed or broken by analytics. */
  var LOG_ENDPOINT = 'https://llmedico-meilisearch-sync.llmedico.workers.dev/search-log';
  var reported = {};

  function report(q, hits, source) {
    q = String(q == null ? '' : q).trim();
    if (!q) return;
    // Both recording points can fire for one search; only send it once per page.
    var key = q.toLowerCase() + '|' + source;
    if (reported[key]) return;
    reported[key] = 1;

    try {
      var payload = JSON.stringify({ q: q, hits: hits, source: source, page: location.pathname });
      /* sendBeacon so the request survives the page navigating away — which is exactly what happens
         on submit. Falls back to fetch+keepalive where it is unavailable.

         ⚠️ text/plain, NOT application/json. sendBeacon can only send CORS-SAFELISTED content types
         (text/plain, application/x-www-form-urlencoded, multipart/form-data). application/json makes
         it a non-simple cross-origin request needing a preflight, and a beacon cannot preflight — so
         the browser drops it SILENTLY. No console error, no network entry, nothing arrives. Shipped
         that way first and the D1 table stayed empty while a direct fetch to the same endpoint
         returned 204 (2026-09-05). The worker's request.json() parses the body regardless of the
         declared type, so the label costs nothing. */
      if (navigator.sendBeacon) {
        navigator.sendBeacon(LOG_ENDPOINT, new Blob([payload], { type: 'text/plain' }));
      } else {
        fetch(LOG_ENDPOINT, { method: 'POST', body: payload, keepalive: true,
          headers: { 'Content-Type': 'text/plain' } }).catch(function () {});
      }
    } catch (e) { /* analytics must never throw into the search path */ }

    /* Klaviyo. A DISTINCT metric name on purpose — the live post-search email is keyed to Klaviyo's
       own "Submitted Search" (fired by their Shopify integration off the /search page view), and
       adding to or renaming that metric risks breaking a flow that is sending today. This is
       additive: it carries our real Meilisearch term and result count, so a flow can be pointed at
       it to personalise ("Based on your search for X…") once the owner is ready to switch. */
    /* ⚠️ SUBMIT PATH DOES NOT SEND DIRECTLY — it hands off to the next page. See stashForNextPage()
       below for why; sending here loses the event. */
    if (source === 'submit') { stashForNextPage(q); return; }
    trackKlaviyo(q, hits);
  }

  /* ---- Klaviyo delivery ------------------------------------------------------------------------
     Diagnosed 2026-09-20, after the email team reported the custom metric under-counting Shopify's
     "Submitted Search" and asked us to "resolve the lower event count".

     TWO independent leaks, both on the SUBMIT path (32% of logged searches — 2,000 of 6,249 rows in
     search_log, measured, not estimated):

     1. NAVIGATION CANCELS THE REQUEST. klaviyo.push(['track']) sends an ordinary async XHR. The
        submit listener runs, then the browser immediately navigates to /search — and an in-flight
        XHR dies with the page that issued it. The D1 write beside it survives only because it uses
        navigator.sendBeacon, which exists precisely for this; Klaviyo's SDK does not use one. So the
        row landed in D1 and the Klaviyo event did not, which is exactly the asymmetry the email team
        is looking at.

     2. NO QUEUE WHEN KLAVIYO HAS NOT LOADED. Every Klaviyo script on this theme is `async`, and
        `window.klaviyo` is NOT an array stub here (verified live: Array.isArray(window.klaviyo) ===
        false). So before onsite.js finishes, `window.klaviyo` is undefined and the old one-shot
        `if (window.klaviyo)` check silently skipped the event with no retry. Klaviyo's own
        Submitted Search never loses this race because Klaviyo fires it itself, after its own bundle
        is ready.

     Fixes: withKlaviyo() waits (bounded) instead of checking once, and the submit path defers to the
     next page load rather than racing a navigation it cannot win. ------------------------------ */

  var PENDING_KEY = 'llm_pending_search_track';

  /* Bounded poll, same shape as waitForCount. Gives up rather than holding a reference forever. */
  function withKlaviyo(cb) {
    var tries = 0;
    (function look() {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') return cb(window.klaviyo);
      if (++tries > 20) return;                    // ~5s; Klaviyo is not coming, drop it
      setTimeout(look, 250);
    })();
  }

  function trackKlaviyo(q, hits) {
    /* A DISTINCT metric name on purpose — the live post-search email is keyed to Klaviyo's own
       "Submitted Search" (fired by their Shopify integration off the /search page view), and adding
       to or renaming that metric risks breaking a flow that is sending today. This is additive: it
       carries our real Meilisearch term and result count.

       ⚠️ Klaviyo confirmed (2026-09-20, via the email team) that a custom metric CANNOT enrich or
       populate properties on Shopify's Submitted Search — each event belongs to exactly one metric,
       and a flow cannot reference properties from another one. So this metric is not a supplement to
       theirs; it is the alternative trigger, and it has to stand on its own. */
    try {
      withKlaviyo(function (k) {
        k.push(['track', 'Searched Site (LL Medico)', {
          SearchTerm: q,
          ResultCount: typeof hits === 'number' ? hits : null,
          /* ⚠️ UNKNOWN must stay unknown, not become `false`. On the submit path `hits` is null,
             and `null === 0` is false — which asserts "this search DID find something" about a
             search we never measured. That is the unsafe direction for the only job this property
             has: suppressing a "based on your search" email when the search found nothing. A null
             can be filtered for; a confident false cannot.

             ⚠️⚠️ CONSEQUENCE FOR THE FLOW: a `ResultCount > 0` trigger filter DROPS every null.
             That is correct behaviour (we genuinely do not know the count), but it means such a
             filter silently excludes the deferred submit-path events below — so the filter is not
             free, it costs volume. Tell whoever builds the flow. */
          ZeroResults: typeof hits === 'number' ? hits === 0 : null,
          SearchedFrom: location.pathname,
        }]);
      });
    } catch (e) { /* analytics must never throw into the search path */ }
  }

  /* Park the term and let the NEXT page send it. sessionStorage survives the navigation that kills
     the XHR; a beacon cannot be used because Klaviyo's endpoint needs its SDK's own signing. */
  function stashForNextPage(q) {
    try {
      sessionStorage.setItem(PENDING_KEY, JSON.stringify({ q: q, ts: Date.now() }));
    } catch (e) { /* private mode: lose the event rather than break the search */ }
  }

  function flushPendingTrack() {
    var pending = null;
    try {
      var raw = sessionStorage.getItem(PENDING_KEY);
      if (!raw) return;
      sessionStorage.removeItem(PENDING_KEY);      // remove FIRST: a throw below must not re-send
      pending = JSON.parse(raw);
    } catch (e) { return; }
    if (!pending || !pending.q) return;

    // Stale guard: a tab restored hours later must not report an ancient search as if it just happened.
    if (Date.now() - (pending.ts || 0) > 10 * 60 * 1000) return;

    /* ⚠️ DE-DUPE. If this submit landed on /search for the SAME term, recordTerm() is already
       reporting it with a REAL result count — a strictly better event. Sending both would double the
       metric and hand the flow one copy with ResultCount null, which is worse than either alone.
       Drop ours and let the results path win. */
    if (/\/search/.test(location.pathname)) {
      var landedQ = new URLSearchParams(location.search).get('q') || '';
      if (landedQ.trim().toLowerCase() === String(pending.q).trim().toLowerCase()) return;
    }

    // Survivors are the real prize: searches where the customer went straight from the dropdown to a
    // product and never loaded /search at all. Those were invisible to BOTH metrics until now.
    trackKlaviyo(pending.q, null);
  }

  /* The count line is rendered by Meilisearch after its request resolves. Poll briefly for it and
     give up rather than logging a wrong number.

     ⚠️ Read the COUNT ELEMENT, never document.body.textContent. The page carries a hidden
     zero-results block at all times, so `/no results/i.test(body.textContent)` is TRUE even on a
     page showing 67 products (measured 2026-09-05). Matching on the whole body would have recorded
     healthy searches as zero-result ones and quietly corrupted the single report this table exists
     to produce. */
  function waitForCount(cb) {
    var tries = 0;
    (function look() {
      var el = document.querySelector('.llm-meili-count');
      if (el) {
        var t = el.textContent || '';
        var m = t.match(/of\s+([\d,]+)\s+results/i);
        if (m) return cb(Number(m[1].replace(/,/g, '')));
        if (/\b0\s+results|no results/i.test(t)) return cb(0);
      }
      // Fallback: the document title is authoritative too ("Search: 67 results found for …").
      var tm = document.title.match(/([\d,]+)\s+results found/i);
      if (tm) return cb(Number(tm[1].replace(/,/g, '')));
      if (++tries > 20) return cb(null);          // unknown, logged as null rather than guessed
      setTimeout(look, 250);
    })();
  }

  /* Capture phase: the theme's own search scripts handle submit too, and a listener that bubbles
     would be skipped entirely if any of them called stopPropagation. Delegated from document so it
     covers every search form on the page — header, mobile overlay, the search page's own bar —
     including any rendered after this script runs. */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches) return;
    if (!form.matches('form[action*="/search"], form.search')) return;
    var input = form.querySelector('input[name="q"]');
    if (!input) return;
    storeTerm(input.value);
    /* hits unknown here — the customer is leaving before results render. Logged as null rather than
       0, because 0 means "we found nothing", which is a completely different fact. */
    report(input.value, null, 'submit');
  }, true);

  // ---- rendering -------------------------------------------------------------------------

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Ask the CDN for a thumbnail instead of shipping the full-size social image — og:image is
     typically 1200px wide and there can be twelve of them in the strip.

     This has to REWRITE the query string, not append to it. og:image already carries
     "width=1200&height=1200&crop=center"; appending "&width=160" left a duplicate width, and the
     CDN honoured the new width while keeping height=1200 — returning a 160x1200 canvas with the
     square pack shot letterboxed into a sliver. That was the owner's "image is not showing
     properly" (2026-09-05). Dropping height and crop lets the CDN scale to width and keep the
     real aspect ratio.

     Also forces https: Shopify hands og:image back as "http://" here, and while Chrome silently
     upgrades a same-host image, browsers that block mixed content instead would show nothing. */
  function thumb(url) {
    if (!url) return '';
    try {
      var u = new URL(url, location.href);
      u.protocol = 'https:';
      u.searchParams.delete('height');
      u.searchParams.delete('crop');
      u.searchParams.set('width', '160');
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  var ICON_SEARCH = '<svg class="llm-sr__mag" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
    '<circle cx="7" cy="7" r="4.5" stroke="currentColor" stroke-width="1.5"/>' +
    '<path d="M10.5 10.5L14 14" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
  var ICON_X = '<svg viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
    '<path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  function viewedHtml() {
    /* Skip whatever product the customer is looking at right now — "where you left off" should not
       offer them the page they are standing on. */
    var here = (location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
    var list = read(VIEW_KEY).filter(function (v) { return v && v.handle && v.handle !== here; });
    if (!list.length) return '';

    var tiles = list.map(function (v) {
      return '<a class="llm-sr__tile" href="' + esc(v.url) + '" title="' + esc(v.title) + '">' +
        /* eager, not lazy: the markup is only built when the panel opens, so there is nothing to
           defer — lazy just added a viewport check and a visible pop-in on a strip the customer is
           already looking at. At most twelve 160px thumbnails, cached after the first open. */
        (v.image ? '<img src="' + esc(thumb(v.image)) + '" alt="' + esc(v.title) + '" loading="eager" decoding="async" width="72" height="72">' : '') +
        '</a>';
    }).join('');

    return '<div class="llm-search-empty__section llm-sr__section">' +
      '<p class="llm-search-empty__heading">Pick up where you left off</p>' +
      '<div class="llm-sr__strip-wrap">' +
        '<button type="button" class="llm-sr__arrow llm-sr__arrow--prev" data-llm-sr-scroll="-1" aria-label="Scroll left" hidden>' +
          '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
        '<div class="llm-sr__strip" data-llm-sr-strip>' + tiles + '</div>' +
        '<button type="button" class="llm-sr__arrow llm-sr__arrow--next" data-llm-sr-scroll="1" aria-label="Scroll right" hidden>' +
          '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
      '</div></div>';
  }

  function termsHtml() {
    var list = read(TERM_KEY).filter(Boolean);
    if (!list.length) return '';

    var rows = list.map(function (t) {
      /* js-search-link matches the curated "Popular searches" terms above, so these rows join the
         panel's existing arrow-key navigation instead of being a dead zone inside it. */
      return '<li class="llm-sr__row">' +
        '<a class="llm-sr__term js-search-link" href="/search?q=' + encodeURIComponent(t) + '">' +
          ICON_SEARCH + '<span>' + esc(t) + '</span></a>' +
        '<button type="button" class="llm-sr__remove" data-llm-sr-remove="' + esc(t) + '" ' +
          'aria-label="Remove ' + esc(t) + ' from recent searches">' + ICON_X + '</button>' +
      '</li>';
    }).join('');

    return '<div class="llm-search-empty__section llm-sr__section">' +
      '<p class="llm-search-empty__heading">Your recent searches</p>' +
      '<ul class="llm-sr__terms" role="list">' + rows + '</ul></div>';
  }

  /* Called by predictive-search.js right after it copies the server-rendered empty state into the
     results panel. Returns quietly when there is no history — a new visitor sees exactly what they
     see today. */
  function prepend(resultsEl) {
    if (!resultsEl) return;
    var html = viewedHtml() + termsHtml();
    if (!html) return;

    var host = resultsEl.querySelector('.llm-search-empty') || resultsEl.querySelector('.predictive-search__results');
    if (!host) return;
    host.insertAdjacentHTML('afterbegin', html);
    syncArrows(host);
  }

  // ---- strip arrows ----------------------------------------------------------------------

  /* Shown only when the strip actually overflows. The owner has asked twice for visible arrows on
     horizontally-scrolling rows rather than relying on the customer discovering the drag. */
  function syncArrows(scope) {
    var strip = (scope || document).querySelector('[data-llm-sr-strip]');
    if (!strip) return;
    var wrap = strip.parentElement;
    var prev = wrap.querySelector('.llm-sr__arrow--prev');
    var next = wrap.querySelector('.llm-sr__arrow--next');
    var overflow = strip.scrollWidth - strip.clientWidth;
    if (overflow <= 2) { prev.hidden = true; next.hidden = true; return; }
    prev.hidden = strip.scrollLeft <= 2;
    next.hidden = strip.scrollLeft >= overflow - 2;
  }

  document.addEventListener('scroll', function (e) {
    if (e.target && e.target.matches && e.target.matches('[data-llm-sr-strip]')) syncArrows(e.target.parentElement);
  }, true);

  // ---- interaction -----------------------------------------------------------------------

  document.addEventListener('click', function (e) {
    var scroller = e.target.closest && e.target.closest('[data-llm-sr-scroll]');
    if (scroller) {
      e.preventDefault();
      var strip = scroller.parentElement.querySelector('[data-llm-sr-strip]');
      if (strip) strip.scrollBy({ left: (strip.clientWidth * 0.8) * Number(scroller.dataset.llmSrScroll), behavior: 'smooth' });
      return;
    }

    var rm = e.target.closest && e.target.closest('[data-llm-sr-remove]');
    if (!rm) return;
    /* stopPropagation as well as preventDefault: the row is a link and the panel closes on clicks
       outside a result, so without it the X both follows the search and dismisses the panel. */
    e.preventDefault();
    e.stopPropagation();
    var term = rm.getAttribute('data-llm-sr-remove');
    write(TERM_KEY, read(TERM_KEY).filter(function (t) { return String(t) !== term; }));

    var row = rm.closest('.llm-sr__row');
    var list = row && row.parentElement;
    if (row) row.remove();
    // Last one gone: drop the heading too, rather than leave an empty "Recent searches".
    if (list && !list.querySelector('.llm-sr__row')) {
      var section = list.closest('.llm-sr__section');
      if (section) section.remove();
    }
  });

  // ---- boot ------------------------------------------------------------------------------

  window.llmSearchRecent = { prepend: prepend };

  recordView();
  recordTerm();
  /* ⚠️ AFTER recordTerm(), not before. recordTerm() is what makes this page's own (better, count-
     bearing) event; flushPendingTrack() checks the landed URL to decide whether to suppress the
     deferred copy, so it has to run in a world where that decision is already determined. Ordering
     them the other way risks sending both for the same search. */
  flushPendingTrack();
})();
